import { createSelector } from 'reselect';

// ------------------------------------
// Selectors
// ------------------------------------
export const STORE_KEY = 'positions';
export const selectPositionsState = state => state[STORE_KEY];
export const selectPositions = (state, type) => {
  const positions = selectPositionsState(state)[type];
  if (!positions) {
    throw new Error(
      `selectPositions: No positions found for ${type}. Valid choices are [stats, search, projections, projectionsCombined]`,
    );
  }

  return positions;
};

export const selectPositionsStatsMap = (state, view) =>
  (selectPositionsState(state) && selectPositionsState(state).positionStatsMap ?
    selectPositionsState(state).positionStatsMap[view] : null);

export const getColGroups = (state, view, position) => {
  const positionStatsMap = selectPositionsStatsMap(state, view);
  const pos = position.split(',').join('/');

  return Object.keys(positionStatsMap[pos] || {}).reduce((acc, key) => {
    const group = {};
    const columns = [];
    const fixedColumns = [];
    const statKeyColumns = [];
    group.name = key;

    positionStatsMap[pos][key]
    .sort((valA, valB) => valA.order > valB.order)
    .forEach(val => columns.push(val.stat_abbr) && fixedColumns.push(val.fixed_abbr) && statKeyColumns.push({
      fixedAbbr: val.fixed_abbr,
      displayAbbr: val.stat_abbr,
      groupName: key
    }));
    group.columns = [...columns];
    group.fixedColumns = [...fixedColumns];
    group.statKeyColumns = [...statKeyColumns];
    acc.push(group);
    return acc;
  }, []);
};


const getType = (state, type) => type;
export const selectPositionsForFilterMenu = createSelector(
  [selectPositions, getType],
  (positions, type) => positions.map(position => {
    const formatted = type !== 'search'
      ? position.split(',').join('/')
      : position.split(',')[0];

    return { name: formatted, value: position };
  }),
);

// ------------------------------------
// Reducer
// ------------------------------------
const actions = {};
const initialState = {
  stats: [],
  search: [],
  projections: [],
  projectionsCombined: [],
  error: null,
  hasLoaded: false,
};

const positionsReducer = (state = initialState, action) => {
  const handler = actions[action.type];

  return handler ? handler(state, action) : state;
};

export default positionsReducer;

// ------------------------------------
// Actions
// ------------------------------------
export const LOAD_POSITIONS_REQUEST = `${STORE_KEY}/LOAD_POSITIONS_REQUEST`;
export const LOAD_POSITIONS_SUCCESS = `${STORE_KEY}/LOAD_POSITIONS_SUCCESS`;
export const LOAD_POSITIONS_ERROR = `${STORE_KEY}/LOAD_POSITIONS_ERROR`;
export const LOAD_POSITIONS_STATS_MAP_REQUEST = `${STORE_KEY}/LOAD_POSITIONS_STATS_MAP_REQUEST`;
export const LOAD_POSITIONS_STATS_MAP_SUCCESS = `${STORE_KEY}/LOAD_POSITIONS_STATS_MAP_SUCCESS`;
export const LOAD_POSITIONS_STATS_MAP_ERROR = `${STORE_KEY}/LOAD_POSITIONS_STATS_MAP_ERROR`;

export const loadPositionsRequest = () => ({ type: LOAD_POSITIONS_REQUEST });
export const loadPositionsSuccess = ({ search, stats, projections }) => ({
  type: LOAD_POSITIONS_SUCCESS,
  payload: {
    search: search.positions,
    stats: stats.positions,
    projections: projections.positions,
    // TODO these aren't consistent with the other combined positions, have kkoston update the api
    // to return all positions as an array of strings, and combined positions should be comma sep
    // IE: ["QB", "RB", "QB,RB"]
    projectionsCombined: projections.combined_positions.map(combined => combined.join(',')),
  },
});
export const loadPositionsError = error => ({
  type: LOAD_POSITIONS_ERROR,
  payload: { error },
});

export const loadPositionsStatsMapRequest = view =>
  ({ type: LOAD_POSITIONS_STATS_MAP_REQUEST, payload: view });
export const loadPositionsStatsMapSuccess = (view, { mappings }) => ({
  type: LOAD_POSITIONS_STATS_MAP_SUCCESS,
  payload: {
    view,
    mappings,
  },
});
export const loadPositionsStatsMapError = error => ({
  type: LOAD_POSITIONS_STATS_MAP_ERROR,
  payload: { error },
});

actions[LOAD_POSITIONS_SUCCESS] = (state, { payload }) => ({ ...state,
  search: payload.search,
  stats: payload.stats,
  projections: payload.projections,
  projectionsCombined: payload.projectionsCombined,
  hasLoaded: true,
});
actions[LOAD_POSITIONS_ERROR] = (state, { payload: { error } }) => ({ ...state,
  error,
});

actions[LOAD_POSITIONS_STATS_MAP_SUCCESS] = (state, { payload }) => ({ ...state,
  positionStatsMap: {
    ...state.positionStatsMap,
    [payload.view]: payload.mappings,
  },
});
actions[LOAD_POSITIONS_STATS_MAP_ERROR] = (state, { payload: { error } }) => ({ ...state,
  error,
});

export const getPositions = async (dispatch, getState, { api }) => {
  try {
    dispatch(loadPositionsRequest());
    const positions = await api.lookups.getPositions();

    return dispatch(loadPositionsSuccess(positions));
  } catch (error) {
    return dispatch(loadPositionsError(error));
  }
};

export const getPositionsStatsMap = view => async (dispatch, getState, { api }) => {
  try {
    dispatch(loadPositionsStatsMapRequest(view));
    const positionStatsMap = await api.lookups.getPositionStatsMap(view);
    return dispatch(loadPositionsStatsMapSuccess(view, positionStatsMap));
  } catch (error) {
    return dispatch(loadPositionsStatsMapError(error));
  }
};



// WEBPACK FOOTER //
// ./src/store/reducers/positions.js